var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { publishProposal, takeProposalOffline, withdrawProposalFromApproval } from '../../Util/ApiService';
import Loader from '../Loader/Loader';
import displayToast from '../../Util/Toast';
import { setCookie } from '../../Util/data';
import Tooltip from '../Tooltip/Tooltip';
import useOnclickOutside from 'react-cool-onclickoutside';
// Cookie name for proposal publish or sent for approval
const proposalPublishOrSentForApprovalCookie = 'proposalPublishOrSentForApprovalCookie';
const MyPitchesCard = ({ checkoutUrl, hideModal, loadingShareUrls, nudgeCount, packageChooseUrl, pitchEditUrl, proposalExtrasUrl, purchaseNudgesUrl, proposal, publishProposalUrl, selectedProposalToPublish, submitForApprovalUrl, subscriptionTypeCodes = null, takeOfflineUrl, toggleShareModal, toggleDeleteModal, withdrawFromApprovalUrl }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    const { t } = useTranslation();
    // Local, mutable copy of the proposal. This is the single source of truth for
    // everything the card renders (status, banners, button label + action), so
    // updating it after an action re-renders the card in place with no reload.
    const [pitch, setPitch] = useState(proposal);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedProposalIdToPublish, setSelectedProposalIdToPublish] = useState(null);
    const [showWarning, setShowWarning] = useState(false);
    const [showContextMenu, setShowContextMenu] = useState(false);
    // A pitch has no paid package when it is on the free tier (novice+) or no
    // subscription type has been selected yet.
    const hasNoPaidPackage = ((_a = pitch.subscriptionType) === null || _a === void 0 ? void 0 : _a.code) === null ||
        ((_b = pitch.subscriptionType) === null || _b === void 0 ? void 0 : _b.code) === (subscriptionTypeCodes === null || subscriptionTypeCodes === void 0 ? void 0 : subscriptionTypeCodes.novicePlus);
    // A package picked but not paid for is not on the pitch's subscription yet, so fall back to the
    // one waiting in the cart rather than reporting no package at all.
    const packageName = !hasNoPaidPackage
        ? pitch.subscriptionType.name
        : (_d = (_c = pitch.unpaidSubscriptionTypeInCart) === null || _c === void 0 ? void 0 : _c.name) !== null && _d !== void 0 ? _d : '-';
    const displayUpgradeBanner = pitch.active &&
        (((_e = pitch.subscriptionType) === null || _e === void 0 ? void 0 : _e.code) === (subscriptionTypeCodes === null || subscriptionTypeCodes === void 0 ? void 0 : subscriptionTypeCodes.pro) ||
            ((_f = pitch.subscriptionType) === null || _f === void 0 ? void 0 : _f.code) === (subscriptionTypeCodes === null || subscriptionTypeCodes === void 0 ? void 0 : subscriptionTypeCodes.globalPro));
    // A live novice+ pitch has no publish/pause action available.
    const isNovicePlusActive = pitch.active &&
        ((_g = pitch.subscriptionType) === null || _g === void 0 ? void 0 : _g.code) === (subscriptionTypeCodes === null || subscriptionTypeCodes === void 0 ? void 0 : subscriptionTypeCodes.novicePlus);
    const statusText = pitch.active
        ? t('myPitches.live')
        : pitch.pendingApproval
            ? t('myPitches.awaitingApproval')
            : pitch.expired
                ? t('myPitches.expired')
                : t('myPitches.draft');
    const btnTitle = pitch.pendingApproval
        ? t('myPitches.cancelSubmission')
        : pitch.expired
            ? t('proposalDashboard.renew')
            : pitch.active
                ? t('myPitches.pause')
                : t('myPitches.publish');
    const isBtnDisabled = (() => {
        // Awaiting approval ("Cancel Submission") and expired ("Renew") stay
        // clickable.
        if (pitch.pendingApproval || pitch.expired) {
            return false;
        }
        // Can't publish without a paid, paid-for package.
        if (hasNoPaidPackage || pitch.unpaidSubscriptionInCart) {
            return true;
        }
        if (pitch.submittableForApproval) {
            return false;
        }
        return !pitch.publishable && !pitch.active;
    })();
    const hideContextMenu = () => {
        setShowContextMenu(false);
    };
    const ref = useOnclickOutside(() => {
        hideContextMenu();
    });
    const takeProposalLive = (proposalId) => __awaiter(void 0, void 0, void 0, function* () {
        hideModal();
        setIsLoading(true);
        const submittingForApproval = pitch.submittableForApproval;
        const url = submittingForApproval
            ? submitForApprovalUrl.replace('_PROPOSAL_ID_', proposalId.toString())
            : publishProposalUrl.replace('_PROPOSAL_ID_', proposalId.toString());
        try {
            const result = yield publishProposal(url);
            if (result) {
                setCookie(proposalPublishOrSentForApprovalCookie, null, true);
                if (submittingForApproval) {
                    // First-time publish goes into the approval queue, not live yet.
                    // Surface the awaiting-approval banner and offer to cancel it.
                    setPitch((prev) => (Object.assign(Object.assign({}, prev), { pendingApproval: true, draft: false })));
                }
                else {
                    // Re-publishing an already-approved pitch takes it live immediately.
                    setPitch((prev) => (Object.assign(Object.assign({}, prev), { active: true, expired: false, draft: false })));
                }
            }
        }
        catch (error) {
            displayToast(t('toast.error'), 'error');
        }
        finally {
            setIsLoading(false);
        }
    });
    const handleCancelSubmission = (proposalId) => __awaiter(void 0, void 0, void 0, function* () {
        setIsLoading(true);
        const url = withdrawFromApprovalUrl.replace('_PROPOSAL_ID_', proposalId.toString());
        try {
            const result = yield withdrawProposalFromApproval(url);
            if (result) {
                // Withdrawing puts the pitch back to draft in the backend, so the user
                // can re-submit it for approval whenever they want. Re-enable the
                // Publish action (the pending pitch reported it as non-submittable).
                setPitch((prev) => (Object.assign(Object.assign({}, prev), { pendingApproval: false, draft: true, submittableForApproval: true })));
            }
        }
        catch (error) {
            displayToast(t('toast.error'), 'error');
        }
        finally {
            setIsLoading(false);
        }
    });
    const handleProposalOffline = (proposalId) => __awaiter(void 0, void 0, void 0, function* () {
        // eslint-disable-next-line no-restricted-globals, no-undef
        if (pitch.expired && typeof window !== 'undefined') {
            // eslint-disable-next-line no-restricted-globals, no-undef
            return (window.location.href = packageChooseUrl.replace('_PROPOSAL_ID_', proposalId.toString()));
        }
        setIsLoading(true);
        const url = takeOfflineUrl.replace('_PROPOSAL_ID_', proposalId.toString());
        try {
            const result = yield takeProposalOffline(url);
            if (result) {
                // Pausing returns the pitch to draft; it can be published again.
                setPitch((prev) => (Object.assign(Object.assign({}, prev), { active: false, draft: true, publishable: true })));
            }
        }
        catch (error) {
            displayToast(t('toast.error'), 'error');
        }
        finally {
            setIsLoading(false);
        }
    });
    const handlePublishProposal = (proposalId) => {
        var _a;
        // eslint-disable-next-line no-restricted-globals, no-undef
        if (pitch.expired && typeof window !== 'undefined') {
            // eslint-disable-next-line no-restricted-globals, no-undef
            return (window.location.href = packageChooseUrl.replace('_PROPOSAL_ID_', proposalId.toString()));
        }
        // eslint-disable-next-line no-restricted-globals, no-undef
        if (pitch.unpaidSubscriptionInCart && typeof window !== 'undefined') {
            // eslint-disable-next-line no-restricted-globals, no-undef
            return (window.location.href = checkoutUrl);
        }
        if ((!(pitch.publishable || pitch.active) &&
            ((_a = pitch.subscriptionType) === null || _a === void 0 ? void 0 : _a.code) === null) ||
            pitch.subscriptionType.code === (subscriptionTypeCodes === null || subscriptionTypeCodes === void 0 ? void 0 : subscriptionTypeCodes.novicePlus)) {
            return setShowWarning(true);
        }
        takeProposalLive(proposalId);
    };
    const renderDescriptionElement = (title, description, icon, link = '#', hasLink = false, isOutlined = false, customClassNames = '') => {
        return (React.createElement("div", { className: `w-full flex items-center justify-between py-3 ${customClassNames}` },
            React.createElement("div", { className: "flex items-center gap-2" },
                React.createElement("span", { className: `${isOutlined ? 'material-symbols-outlined' : 'material-icons'} text-gray-800 text-2xl"` }, icon),
                hasLink ? (React.createElement("a", { href: link, className: "text-lg leading-6 text-gray-700 underline" }, title)) : (React.createElement("span", { className: "text-lg leading-6 text-gray-700 whitespace-nowrap" }, title))),
            React.createElement("p", { className: "text-gray-800 font-semibold text-xl" }, description)));
    };
    useEffect(() => {
        setSelectedProposalIdToPublish(selectedProposalToPublish);
        if (selectedProposalIdToPublish &&
            selectedProposalIdToPublish === proposal.proposalId) {
            takeProposalLive(selectedProposalIdToPublish);
        }
    }, [selectedProposalToPublish]);
    return (React.createElement("div", { key: proposal.proposalId, className: "bg-white w-full shadow-md p-6 flex flex-col gap-12" },
        React.createElement("div", { className: "w-full flex items-start justify-between" },
            React.createElement("div", { className: "flex md:items-center items-start gap-6 md:flex-row flex-col" },
                React.createElement("img", { src: proposal.logoUri, alt: proposal.title, className: "w-16 h-16 shrink-0 rounded-lg object-cover object-center" }),
                React.createElement("h3", { className: "text-gray-800 font-semibold md:text-2xl text-xl leading-8" }, proposal.title)),
            React.createElement("div", { className: "flex items-center gap-2 justify-end md:relative absolute right-10 md:right-0" },
                React.createElement("div", { className: "relative" },
                    React.createElement("button", { ref: ref, onClick: () => {
                            return setShowContextMenu(true);
                        }, type: "button", className: "w-10 h-10 shrink-0 rounded-md text-gray-800 bg-gray-200 hover:bg-gray-300 focus:outline-none inline-flex items-center justify-center disabled:cursor-not-allowed" },
                        React.createElement("span", { className: "material-icons text-xl" }, "more_horiz")),
                    showContextMenu && (React.createElement("div", { ref: ref, className: "absolute z-10 w-[223px] flex flex-col bg-white rounded-sm shadow-4xl left-0 md:right-0 mt-2 py-3 px-2.5 whitespace-nowrap" },
                        React.createElement("button", { onClick: () => {
                                return toggleShareModal(proposal.proposalId);
                            }, disabled: loadingShareUrls, type: "button", className: "w-full rounded-sm text-base text-gray-800 bg-transparent hover:bg-gray-100 focus:outline-none p-2 leading-6 inline-flex gap-2 items-center disabled:cursor-not-allowed" },
                            loadingShareUrls ? (React.createElement(Loader, { customColor: "text-gray-800", title: t('auth.inbox.loading') })) : (React.createElement("span", { className: "material-icons text-xl" }, "share")),
                            React.createElement("span", { className: "text-gray-800 text-base leading-6" }, t('myPitches.sharePitch'))),
                        React.createElement("button", { onClick: () => {
                                return toggleDeleteModal(proposal.proposalId);
                            }, type: "button", className: "w-full rounded-sm text-base text-gray-800 bg-transparent hover:bg-gray-100 focus:outline-none p-2 leading-6 inline-flex gap-2 items-center disabled:cursor-not-allowed" },
                            React.createElement("span", { className: "material-icons text-xl" }, "delete"),
                            React.createElement("span", { className: "text-gray-800 text-base leading-6" }, t('entrepreneurDashboard.modals.deletePitch.title')))))),
                React.createElement(Tooltip, { text: t('myPitches.viewPitch'), classNames: "right-30 whitespace-nowrap" },
                    React.createElement("a", { href: proposal.proposalUri, target: "_blank", rel: "noreferrer", className: "w-10 h-10 shrink-0 rounded-md text-gray-800 bg-gray-200 hover:bg-gray-300 focus:outline-none inline-flex items-center justify-center" },
                        React.createElement("span", { className: "material-icons text-xl" }, "visibility"))),
                React.createElement(Tooltip, { text: t('myPitches.editPitch'), classNames: "md:left-20 left-12 whitespace-nowrap" },
                    React.createElement("a", { href: pitchEditUrl.replace('_PROPOSAL_ID_', proposal.proposalId.toString()), className: "w-10 h-10 shrink-0 rounded-md text-gray-700 bg-gray-200 hover:bg-gray-300 focus:outline-none inline-flex items-center justify-center" },
                        React.createElement("span", { className: "material-icons text-xl" }, "edit"))),
                isNovicePlusActive ? (React.createElement(React.Fragment, null)) : (React.createElement("button", { type: "button", disabled: isBtnDisabled || isLoading, onClick: () => {
                        if (pitch.pendingApproval) {
                            return handleCancelSubmission(pitch.proposalId);
                        }
                        return pitch.active
                            ? handleProposalOffline(pitch.proposalId)
                            : handlePublishProposal(pitch.proposalId);
                    }, className: `${isLoading ? 'w-10 h-10' : ''} bg-blue-600 hover:bg-blue-500 text-white py-2 px-4 inline-flex items-center justify-center text-center text-base leading-6 font-semibold rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed` }, isLoading ? (React.createElement(Loader, { title: t('auth.inbox.loading') })) : (btnTitle))))),
        displayUpgradeBanner && (React.createElement("div", { role: "alert", className: "-mt-10 flex items-start gap-4 bg-teal-100 text-teal-800 px-4 py-3 text-base leading-6", "aria-live": "polite" },
            React.createElement("span", { className: "material-symbols-outlined" }, "bolt"),
            React.createElement("div", { className: "w-full flex md:flex-row flex-col items-start gap-2 justify-between" },
                React.createElement("span", null, ((_h = proposal === null || proposal === void 0 ? void 0 : proposal.subscriptionType) === null || _h === void 0 ? void 0 : _h.code) === (subscriptionTypeCodes === null || subscriptionTypeCodes === void 0 ? void 0 : subscriptionTypeCodes.pro)
                    ? t('entrepreneurDashboard.warning.upgradeToGlobalPro.description')
                    : ((_j = proposal === null || proposal === void 0 ? void 0 : proposal.subscriptionType) === null || _j === void 0 ? void 0 : _j.code) ===
                        (subscriptionTypeCodes === null || subscriptionTypeCodes === void 0 ? void 0 : subscriptionTypeCodes.globalPro)
                        ? t('entrepreneurDashboard.warning.upgradeToExecutive.description')
                        : ''),
                React.createElement("a", { href: packageChooseUrl.replace('_PROPOSAL_ID_', proposal.proposalId.toString()), className: "underline whitespace-nowrap" }, ((_k = proposal === null || proposal === void 0 ? void 0 : proposal.subscriptionType) === null || _k === void 0 ? void 0 : _k.code) === (subscriptionTypeCodes === null || subscriptionTypeCodes === void 0 ? void 0 : subscriptionTypeCodes.pro)
                    ? t('entrepreneurDashboard.warning.upgradeToGlobalPro.linkTitle')
                    : ((_l = proposal === null || proposal === void 0 ? void 0 : proposal.subscriptionType) === null || _l === void 0 ? void 0 : _l.code) ===
                        (subscriptionTypeCodes === null || subscriptionTypeCodes === void 0 ? void 0 : subscriptionTypeCodes.globalPro)
                        ? t('entrepreneurDashboard.warning.upgradeToExecutive.linkTitle')
                        : '')))),
        pitch.pendingApproval && (React.createElement("div", { className: "-mt-10 flex items-start gap-4 bg-blue-100 text-blue-800 px-4 py-3 text-base leading-6", role: "alert", "aria-live": "polite" },
            React.createElement("span", { className: "material-icons" }, "info"),
            React.createElement("span", null, t('myPitches.awaitingApprovalBanner')))),
        !pitch.unpaidSubscriptionInCart &&
            (showWarning ||
                (hasNoPaidPackage &&
                    !pitch.active &&
                    !pitch.pendingApproval &&
                    !pitch.expired)) && (React.createElement("div", { className: "-mt-10 flex items-start gap-4 bg-yellow-100 px-4 py-3 text-base text-yellow-800", role: "alert" },
            React.createElement("span", { className: "material-icons" }, "warning"),
            React.createElement("div", { className: "w-full flex md:flex-row flex-col items-start gap-2 justify-between" },
                React.createElement("span", null, t('myPitches.needPaidPackageWarning')),
                React.createElement("a", { href: packageChooseUrl.replace('_PROPOSAL_ID_', proposal.proposalId.toString()), className: "text-yellow-800 underline whitespace-nowrap hover:text-yellow-900" }, t('myPitches.choosePackage'))))),
        pitch.unpaidSubscriptionInCart && (React.createElement("div", { className: "-mt-10 flex items-start gap-4 bg-yellow-100 px-4 py-3 text-base text-yellow-800", role: "alert" },
            React.createElement("span", { className: "material-icons" }, "warning"),
            React.createElement("div", { className: "w-full flex md:flex-row flex-col items-start gap-2 justify-between" },
                React.createElement("span", null, t('myPitches.needPaymentWarning')),
                React.createElement("a", { href: checkoutUrl, className: "text-yellow-800 underline whitespace-nowrap hover:text-yellow-900" }, t('myPitches.payNow'))))),
        React.createElement("div", { className: "grid md:grid-cols-2 grid-cols-1 md:gap-16" },
            React.createElement("div", { className: "w-full flex flex-col md:divide-y divide-gray-300" },
                renderDescriptionElement(t('myPitches.progress'), `${proposal.proposalCompletePercentage}% ${t('myPitches.complete')}`, 'av_timer', pitchEditUrl.replace('_PROPOSAL_ID_', proposal.proposalId.toString()), true, false, 'md:border-0 border-b border-gray-300'),
                renderDescriptionElement(t('myPitches.package'), packageName, 'business_center', packageChooseUrl.replace('_PROPOSAL_ID_', proposal.proposalId.toString()), true, false, 'md:border-0 border-b border-gray-300'),
                renderDescriptionElement(t('myPitches.status'), statusText, 'insert_emoticon', pitch.expired
                    ? packageChooseUrl.replace('_PROPOSAL_ID_', proposal.proposalId.toString())
                    : '#', pitch.expired, false, 'md:border-0 border-b border-gray-300')),
            React.createElement("div", { className: "w-full flex flex-col divide-y divide-gray-300" },
                renderDescriptionElement(t('myPitches.nudges'), nudgeCount, 'handshake', purchaseNudgesUrl, true, true),
                renderDescriptionElement(t('myPitches.boosters'), proposal.boosterCount, 'rocket_launch', proposalExtrasUrl.replace('_PROPOSAL_ID_', proposal.proposalId.toString()), true, true)))));
};
export default MyPitchesCard;
